import React, { useEffect, useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { ChevronDown, Eye, PlaySquare, UserRound, Wand2 } from 'lucide-react';

// What Scene Edit is, and how it differs from the quick-fix tools. Shown once as a
// popup on first visit (a dark bottom sheet on phones, a card on desktop), and always
// available from the collapsible cream panel on top. Copy is Joe's own (Oct 10);
// keep it as written.
const SEEN_KEY = 'sceneEditIntroSeen';
const OPEN_KEY = 'sceneEditIntroOpen';

const readFlag = (key: string): string | null => {
  try { return localStorage.getItem(key); } catch { return null; }
};
const writeFlag = (key: string, value: string) => {
  try { localStorage.setItem(key, value); } catch { /* private window */ }
};

const SHEET = {
  bg: '#15130f',
  ink: '#f6f1e7',
  ink2: '#d9d2c4',
  ink3: '#b3ab9c',
  line: '#36322b',
  accent: '#ffd600',
};

const POINTS = [
  { icon: UserRound, title: 'Change the setting, not the actor.', line: 'Scene Edit transforms the environment around you while working to preserve your face, wardrobe, movement, and performance.' },
  { icon: PlaySquare, title: 'Make something worth showing.', line: 'Create scenes for your reel, social media, class projects, or your own films, without needing a crew or expensive locations.' },
  { icon: Eye, title: 'See it before you commit.', line: "Try a 5-second preview for just 3 tokens. When you're happy with the direction, create a finished scene up to 30 seconds long." },
];

const PANEL_POINTS = [
  { label: '5-second preview:', text: '3 tokens, ready in about 2 minutes.' },
  { label: 'Full edits:', text: '10 seconds for 15 tokens, 20 seconds for 20, or 30 seconds for 25.' },
  { label: 'Processing:', text: 'A 30-second edit typically takes 10–15 minutes.' },
  { label: 'Your footage:', text: 'You can leave the page while it processes. Finished edits automatically save to My Library.' },
];

export const SceneEditIntro: React.FC = () => {
  const [popupOpen, setPopupOpen] = useState(false);
  const [panelOpen, setPanelOpen] = useState(() => readFlag(OPEN_KEY) === '1');

  useEffect(() => {
    if (!readFlag(SEEN_KEY)) setPopupOpen(true);
  }, []);

  const closePopup = () => {
    writeFlag(SEEN_KEY, '1');
    setPopupOpen(false);
  };

  const togglePanel = () => {
    setPanelOpen((open) => {
      writeFlag(OPEN_KEY, open ? '0' : '1');
      return !open;
    });
  };

  const ink = 'var(--hj-beige-ink)';
  const ink2 = 'var(--hj-beige-ink-2)';
  const line = 'var(--hj-beige-line)';

  return (
    <>
      <section
        data-paper="beige"
        style={{
          background: 'var(--hj-beige)',
          border: `1px solid ${line}`,
          borderRadius: 'var(--hj-r-md)',
          color: ink,
          marginBottom: 16,
        }}
      >
        <button
          type="button"
          onClick={togglePanel}
          aria-expanded={panelOpen}
          className="flex w-full items-center gap-3 text-left"
          style={{ padding: '14px 18px', minHeight: 56, background: 'transparent', border: 0, cursor: 'pointer', color: ink }}
        >
          <Wand2 size={18} strokeWidth={1.8} className="flex-shrink-0" />
          <span className="flex-1 min-w-0">
            <span className="block" style={{ fontSize: 15, fontWeight: 700 }}>Costs, timing & saved edits</span>
          </span>
          <ChevronDown
            size={18}
            className="flex-shrink-0 transition-transform"
            style={{ transform: panelOpen ? 'rotate(180deg)' : 'none' }}
          />
        </button>
        {panelOpen && (
          <div style={{ padding: '0 18px 16px 49px' }}>
            <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
              {PANEL_POINTS.map((p) => (
                <li key={p.label} style={{ fontSize: 14, lineHeight: 1.5, color: ink }}><strong style={{ fontWeight: 700 }}>{p.label}</strong> {p.text}</li>
              ))}
            </ul>
            <button
              type="button"
              onClick={() => setPopupOpen(true)}
              style={{ marginTop: 10, minHeight: 44, padding: 0, background: 'transparent', border: 0, color: ink, fontSize: 14, fontWeight: 600, textDecoration: 'underline', textUnderlineOffset: 3, cursor: 'pointer' }}
            >
              What Scene Edit does
            </button>
          </div>
        )}
      </section>

      <DialogPrimitive.Root open={popupOpen} onOpenChange={(o) => { if (!o) closePopup(); }}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay
            className="fixed inset-0 z-50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
            style={{ background: 'rgba(12, 10, 8, 0.62)' }}
          />
          {/* Bottom sheet on phones, centered card from sm up */}
          <DialogPrimitive.Content
            className="fixed z-50 inset-x-0 bottom-0 top-11 flex flex-col overflow-hidden rounded-t-3xl sm:inset-auto sm:left-1/2 sm:top-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:w-[440px] sm:max-h-[88vh] sm:rounded-3xl data-[state=open]:animate-in data-[state=open]:slide-in-from-bottom-8 sm:data-[state=open]:slide-in-from-bottom-0 sm:data-[state=open]:zoom-in-95 focus:outline-none"
            style={{ background: SHEET.bg, color: SHEET.ink, fontFamily: 'var(--hj-font-ui)' }}
          >
            <div className="flex justify-center sm:hidden" style={{ padding: '10px 0 6px' }}>
              <div style={{ width: 40, height: 4, borderRadius: 2, background: '#4a453d' }} />
            </div>
            <div className="flex-1 overflow-y-auto" style={{ padding: '14px 20px 4px', display: 'flex', flexDirection: 'column', gap: 18 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ fontFamily: 'var(--hj-font-mono)', fontSize: 11.5, letterSpacing: '0.1em', textTransform: 'uppercase', color: SHEET.accent }}>
                  Hey Joe Plus
                </div>
                <DialogPrimitive.Title
                  className="font-hj-display"
                  style={{ margin: 0, fontWeight: 800, fontSize: 30, lineHeight: 1.08, letterSpacing: '-0.02em', color: SHEET.ink }}
                >
                  Your scene. Any setting.
                </DialogPrimitive.Title>
                <DialogPrimitive.Description style={{ margin: 0, fontSize: 15, lineHeight: 1.5, color: SHEET.ink2 }}>
                  Imagine taking a scene you shot in your living room and placing yourself on a rainy New York street, in a candlelit restaurant, or on a film set halfway around the world.
                </DialogPrimitive.Description>
                <p style={{ margin: 0, fontSize: 15, lineHeight: 1.5, fontWeight: 700, color: '#fff' }}>That's Scene Edit.</p>
              </div>

              <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 14 }}>
                {POINTS.map(({ icon: Icon, title, line: text }) => (
                  <li key={title} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                    <Icon size={20} strokeWidth={1.8} color={SHEET.accent} aria-hidden="true" style={{ flexShrink: 0, marginTop: 1 }} />
                    <div style={{ fontSize: 14, lineHeight: 1.45, color: SHEET.ink2 }}>
                      <strong style={{ display: 'block', color: '#fff', fontWeight: 700, marginBottom: 2 }}>{title}</strong>{text}
                    </div>
                  </li>
                ))}
              </ul>

              <p style={{ margin: '0 0 4px', fontSize: 13, lineHeight: 1.45, color: SHEET.ink3 }}>
                Scene Edit is for creative projects, not casting self-tape submissions. Keep your auditions authentic.
              </p>
            </div>

            <div style={{ padding: '14px 20px calc(20px + env(safe-area-inset-bottom))', display: 'flex', flexDirection: 'column', gap: 4, borderTop: '1px solid #2a2721' }}>
              <button
                type="button"
                onClick={closePopup}
                className="font-hj-display"
                style={{ height: 52, border: 0, borderRadius: 14, background: SHEET.accent, color: SHEET.bg, fontWeight: 800, fontSize: 16, cursor: 'pointer' }}
              >
                Try Scene Edit
              </button>
              <DialogPrimitive.Close asChild>
                <button
                  type="button"
                  style={{ height: 44, border: 0, background: 'transparent', color: SHEET.ink2, fontSize: 15, fontWeight: 500, cursor: 'pointer' }}
                >
                  Maybe later
                </button>
              </DialogPrimitive.Close>
            </div>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    </>
  );
};
